import NextLink from 'next/link';

import type { MktMenuDefinition } from '@playbooks/components/mkt/mkt-menu-config';
import { useClient } from '@playbooks/contexts';
import { Icon } from '@playbooks/ui/components/icon';
import { NavigationMenu } from '@playbooks/ui/components/navigation-menu';
import { cn } from '@playbooks/ui/lib/utils';

const MktMenuPanel = ({ menu }: { menu: MktMenuDefinition }) => {
	const { platformHref } = useClient();

	// Theme
	const rootClassName = cn('grid gap-4 p-4', menu.groups.length === 2 ? 'grid-cols-2' : 'grid-cols-3');

	// Render
	return (
		<div className={rootClassName}>
			{menu.groups.map(group => (
				<div key={group.label} className='min-w-0'>
					{group.href ? (
						<NextLink
							href={group.target === '_blank' ? group.href : platformHref(group.href)}
							target={group.target}
							rel={group.target === '_blank' ? 'noopener noreferrer' : undefined}
							className='group mb-2 flex-start w-fit gap-1 px-1 text-sm font-medium text-foreground outline-none transition-colors hover:text-foreground/70 focus-visible:rounded-control focus-visible:ring-2 focus-visible:ring-focus/50'>
							<span>{group.label}</span>
							<Icon
								type='far'
								icon='arrow-up-right'
								className='scale-75 text-[10px] text-foreground-body transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
								aria-hidden
							/>
						</NextLink>
					) : (
						<p className='px-1 pb-2 text-sm font-medium text-foreground'>{group.label}</p>
					)}
					<ul className='space-y-2'>
						{group.items.map(item => (
							<li key={item.href}>
								<NavigationMenu.Link
									render={
										<NextLink
											href={item.target === '_blank' ? item.href : platformHref(item.href)}
											target={item.target}
											rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
										/>
									}
									className='group flex-start-top gap-3 rounded-media border border-border/70 bg-surface px-2.5 py-4 outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:ring-2 focus-visible:ring-focus/50'>
									<span className='flex-middle size-8 shrink-0 rounded-panel bg-surface text-foreground-body shadow-xs'>
										<Icon type={item.iconType || 'far'} icon={item.icon} className='text-xs' aria-hidden />
									</span>
									<span className='min-w-0 grow'>
										<span className='block text-sm font-medium text-foreground'>{item.label}</span>
										<span className='mt-1 block line-clamp-2 text-xs leading-5 text-foreground-body'>
											{item.description}
										</span>
									</span>
								</NavigationMenu.Link>
							</li>
						))}
					</ul>
				</div>
			))}
		</div>
	);
};

export { MktMenuPanel };
